import { Component } from '@angular/core';
import { FormBuilder, FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/internal/operators/take';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SharedService } from '../../../../shared/services/shared.service';
import { environment } from '../../../../../environments/environment';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { UtilsService } from '../../../../shared/services/utils.service';
import { EmailControllerService, SubscriptionModel, TenderiMkBillingInformationDto, TenderiMkRequest } from 'api-notification';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { ManagementCategoryResponse, ManagementTagResponse, PublicCategoryContractNoticesControllerService } from 'api-contract-notices';
import { MatDividerModule } from '@angular/material/divider';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';

interface SubscriptionPackageGroup {
	[key: string]: SubscriptionPackage[];
}

interface SubscriptionPackage {
	title: string;
	group: 'groupMkd' | 'groupXk';
	type?: 'discount';
	text?: string;
	value?: string;
	prices: number[];
	checked?: boolean;
}

type SubscriptionPeriod = '1MONTH' | '3MONTHS' | '6MONTHS' | '12MONTHS';

interface ManagementCategoryWithTags extends ManagementCategoryResponse {
	tags?: ManagementTagWithSelect[];
	checked?: boolean;
	isOpened?: boolean;
}

interface ManagementTagWithSelect extends ManagementTagResponse {
	checked?: boolean;
}
@Component({
	selector: 'app-landing-pricing',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		MatCardModule,
		MatFormFieldModule,
		MatInputModule,
		TranslateModule,
		MatButtonModule,
		ReactiveFormsModule,
		MatProgressSpinnerModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatButtonToggleModule,
		MatCheckboxModule,
		MatIconModule,
		MatExpansionModule,
		MatDividerModule,
		MatSelectModule,
    	MatRadioModule,
    	MatTooltipModule
	],
	templateUrl: './landing-pricing.component.html',
	styleUrl: './landing-pricing.component.scss',
})
export class LandingPricingComponent {
	resellerSettingsContext: any;
	mobileView = false;
	data: any = [];
	selectedSubscriptionPeriod: SubscriptionPeriod = '12MONTHS';
	subscriptionPeriods = ['1MONTH', '3MONTHS', '6MONTHS', '12MONTHS'];
	selectedSubscriptionPackagesGroups: SubscriptionPackageGroup = {
		groupMkd: new Array(),
		groupXk: new Array(),
	};
	subscriptionPackagesGroups: SubscriptionPackageGroup = {
		groupMkd: new Array(),
		groupXk: new Array(),
	};
	discountPackage!: SubscriptionPackage;
	availableSubscriptionGroups: string[] = ['groupMkd', 'groupXk'];
	totalPrice: number = 0;
	formGroup: FormGroup = new FormGroup({});

	categories!: ManagementCategoryWithTags[];
	selectedTags: ManagementTagWithSelect[] = [];

	apiLoading = {
		contactForm: false,
	};

	constructor(
		private fb: FormBuilder,
		private _snackBar: MatSnackBar,
		private sharedService: SharedService,
		private responsive: BreakpointObserver,
		public utils: UtilsService,
		private apiNotification: EmailControllerService,
		private translate: TranslateService,
		private apiPublicCategoryContractNotices: PublicCategoryContractNoticesControllerService
	) {
		this.sharedService.resellerSettingsContext.pipe(takeUntilDestroyed()).subscribe({
			next: response => {
				this.resellerSettingsContext = response;
				this.resellerSettingsContext.reseller = environment.reseller;
				this.data = this.resellerSettingsContext.pricing.data;
				this.discountPackage = this.resellerSettingsContext.pricing.discountPackage;

				this.resellerSettingsContext.pricing.subscriptionPackages.forEach((sp: SubscriptionPackage) => {
					this.subscriptionPackagesGroups[sp.group].push(sp);
				});
			},
		});

		this.formGroup = this.fb.group({
			clientName: [, Validators.required],
			taxId: [],
			contactAddress: [, [Validators.required]],
			contactCity: [, [Validators.required]],
			email: [, [Validators.required, Validators.pattern(this.utils.VALIDATION_PATTERNS.get('E_MAIL') || '')]],
			contactPerson: [, [Validators.required]],
			contactPhone: [, [Validators.required]],
			clientSubscriptions: [],
			clientTagIds: [],
			reseller: this.resellerSettingsContext.reseller.name.toUpperCase(),
			foreignCountry: [false, Validators.required],
			country: [],

      paymentOption: [null, Validators.required],
      firstName: [''],
      lastName: [''],
      termsAndConditions: [false, Validators.requiredTrue],

			useSameInfo: [false],
			billingClientName: [],
			billingTaxId: [],
			billingForeignCountry: [false],
			billingCountry: [],
			billingEmail: [],
			billingContactAddress: [],
			billingContactCity: [],
			billingContactPerson: [],
			billingContactPhone: [],
		});

		this.responsive
			.observe([Breakpoints.XSmall, Breakpoints.Small])
			.pipe(takeUntilDestroyed())
			.subscribe(result => {
				this.mobileView = false;

				if (result.matches) {
					this.mobileView = true;
				}
			});

		this.getCategories();
	}

	selectAllSubscriptionPackage(event: MatCheckboxChange, group: string) {
		if (group === 'ALL') {
			this.availableSubscriptionGroups.forEach(asg => {
				this.selectedSubscriptionPackagesGroups[asg] = [];
				this.subscriptionPackagesGroups[asg].forEach((sp, i) => {
					sp.checked = event.checked;
					this.checkSubscriptionPackage(event, sp, i);
				});
			});
		} else {
			this.selectedSubscriptionPackagesGroups[group] = [];
			this.subscriptionPackagesGroups[group].forEach((sp, i) => {
				sp.checked = event.checked;
				this.checkSubscriptionPackage(event, sp, i);
			});
		}
	}

	checkIsSelected(group?: string) {
		if (group === 'ALL') {
			let subscriptionPackagesCount = 0;
			let selectedSubscriptionPackagesCount = 0;
			this.availableSubscriptionGroups.forEach(asg => {
				subscriptionPackagesCount += this.subscriptionPackagesGroups[asg].length;
				selectedSubscriptionPackagesCount += this.selectedSubscriptionPackagesGroups[asg].length;
			});
			return subscriptionPackagesCount === selectedSubscriptionPackagesCount;
		} else if (group) {
			return this.subscriptionPackagesGroups[group].length === this.selectedSubscriptionPackagesGroups[group].length;
		} else {
			let selectedSubscriptionPackagesCount = 0;
			this.availableSubscriptionGroups.forEach(asg => {
				selectedSubscriptionPackagesCount += this.selectedSubscriptionPackagesGroups[asg].length;
			});
			return selectedSubscriptionPackagesCount > 0;
		}
	}

	checkSubscriptionPackage(event: MatCheckboxChange, subscriptionPackage: SubscriptionPackage, index: number) {
		if (event.checked) {
			this.selectedSubscriptionPackagesGroups[subscriptionPackage.group].push(subscriptionPackage);
		} else {
			this.selectedSubscriptionPackagesGroups[subscriptionPackage.group] = this.selectedSubscriptionPackagesGroups[subscriptionPackage.group].filter(
				(sp, i) => sp.value !== subscriptionPackage.value
			);
			console.log(this.selectedSubscriptionPackagesGroups[subscriptionPackage.group]);
		}
		this.calculateTotalPrice();
	}

	getPricingByPackage(subscriptionPackage: SubscriptionPackage) {
		const pricePosition = this.subscriptionPeriods.indexOf(this.selectedSubscriptionPeriod);
		return subscriptionPackage.prices[pricePosition];
	}

	convertPriceToEuro(price?: number | string) {
		if (!price) {
			return 0;
		}

		const priceToNumber = typeof price === 'string' ? parseInt(price, 10) : price;
		return Math.round(priceToNumber / 62);
	}

	checkSubscriptionPeriod(subscriptionPeriod: SubscriptionPeriod) {
		this.selectedSubscriptionPeriod = subscriptionPeriod;
		this.calculateTotalPrice();
	}

	private calculateTotalPrice() {
		this.selectedSubscriptionPackagesGroups;
		this.totalPrice = 0;
		let overallDiscount = 0;

		let discountInGroup: SubscriptionPackage | undefined = undefined;
		let packagesInGroup: SubscriptionPackage[] = [];
		Object.keys(this.subscriptionPackagesGroups).forEach((spg: string) => {
			discountInGroup = undefined;
			packagesInGroup = [];
			this.subscriptionPackagesGroups[spg].forEach(sp => {
				if (sp.type === 'discount') {
					discountInGroup = sp;
				} else {
					packagesInGroup.push(sp);
				}
			});

			if (packagesInGroup.every(sp => sp.checked) && discountInGroup) {
				this.totalPrice += this.getPricingByPackage(discountInGroup);
				overallDiscount++;
			} else {
				packagesInGroup.forEach((sp, i) => {
					if (sp.checked) {
						this.totalPrice += this.getPricingByPackage(sp);
					}
				});
			}
		});

		if (overallDiscount === Object.keys(this.subscriptionPackagesGroups).length) {
			this.totalPrice = this.getPricingByPackage(this.discountPackage);
		}
	}

	getAdditionalPriceCalculationInfo(subscriptionPackage: SubscriptionPackage, type: 'PERIOD' | 'SAVINGS', group?: string, onlyCalculatedValue?: boolean) {
		if (type === 'PERIOD') {
			const subscriptionPeriod = ['1MONTH', '3MONTHS', '6MONTHS', '12MONTHS'];
			if (this.selectedSubscriptionPeriod !== '1MONTH') {
				return (
					'/ ' +
					this.translate.instant(
						'LANDING.' + this.resellerSettingsContext.reseller.name.toUpperCase() + '.PRICING_PERIOD_' + subscriptionPeriod.indexOf(this.selectedSubscriptionPeriod)
					)
				);
			}
		} else if (type === 'SAVINGS' && group) {
			const discountedPrice = this.getPricingByPackage(subscriptionPackage);
			let totalPrice = 0;
			if (group === 'ALL') {
				this.availableSubscriptionGroups.forEach(asg => {
					this.subscriptionPackagesGroups[asg].forEach(spg => {
						if (spg.type !== 'discount') {
							totalPrice += this.getPricingByPackage(spg);
						}
					});
				});
			} else {
				this.subscriptionPackagesGroups[group].forEach(spg => {
					if (spg.type !== 'discount') {
						totalPrice += this.getPricingByPackage(spg);
					}
				});
			}
			return onlyCalculatedValue
				? totalPrice - discountedPrice
				: this.translate.instant('LANDING.' + this.resellerSettingsContext.reseller.name.toUpperCase() + '.PRICING_SAVINGS') +
						' ' +
						this.utils.formatCurrencyLocale(totalPrice - discountedPrice);
		}
		return;
	}

	onCountryChange() {
		if (this.formGroup.controls['foreignCountry'].value) {
			this.formGroup.controls['country'].setValidators(Validators.required);
		} else {
      this.formGroup.controls['country'].clearValidators();
		}
    this.formGroup.controls['country'].updateValueAndValidity();
	}

  onPaymentOptionChange() {
    if (this.formGroup.controls['paymentOption'].value === 'LINK') {
      this.formGroup.controls['firstName'].setValidators(Validators.required);
      this.formGroup.controls['lastName'].setValidators(Validators.required);
    } else {
      this.formGroup.controls['firstName'].clearValidators();
      this.formGroup.controls['lastName'].clearValidators();
    }
    this.formGroup.controls['firstName'].updateValueAndValidity();
    this.formGroup.controls['lastName'].updateValueAndValidity();
  }

	onUseSameInfoChange() {
		if (this.formGroup.controls['useSameInfo'].value) {
			this.formGroup.controls['billingClientName'].setValue(this.formGroup.controls['clientName'].value);
			this.formGroup.controls['billingTaxId'].setValue(this.formGroup.controls['taxId'].value);
			this.formGroup.controls['billingForeignCountry'].setValue(this.formGroup.controls['foreignCountry'].value);
			this.formGroup.controls['billingCountry'].setValue(this.formGroup.controls['country'].value);
			this.formGroup.controls['billingEmail'].setValue(this.formGroup.controls['email'].value);
			this.formGroup.controls['billingContactAddress'].setValue(this.formGroup.controls['contactAddress'].value);
			this.formGroup.controls['billingContactCity'].setValue(this.formGroup.controls['contactCity'].value);
			this.formGroup.controls['billingContactPerson'].setValue(this.formGroup.controls['contactPerson'].value);
			this.formGroup.controls['billingContactPhone'].setValue(this.formGroup.controls['contactPhone'].value);
		} else {
			this.formGroup.controls['billingClientName'].reset();
			this.formGroup.controls['billingTaxId'].reset();
			this.formGroup.controls['billingForeignCountry'].setValue(false);
			this.formGroup.controls['billingCountry'].reset();
			this.formGroup.controls['billingEmail'].reset();
			this.formGroup.controls['billingContactAddress'].reset();
			this.formGroup.controls['billingContactCity'].reset();
			this.formGroup.controls['billingContactPerson'].reset();
			this.formGroup.controls['billingContactPhone'].reset();
		}
	}

	private getCategories() {
		this.apiPublicCategoryContractNotices
			.getCategoriesWithTagsForClientPublic()
			.pipe(take(1))
			.subscribe({
				next: (response: any) => {
					this.categories = response || [];
					this.categories.pop(); // remove "Ostanati tagovi"
				},
				error: () => {
					this.categories = [];
				},
			});
	}

	accordionAction(action: 'openAll' | 'closeAll' | 'open' | 'close', category?: ManagementCategoryWithTags) {
		switch (action) {
			case 'open':
			case 'close': {
				if (category) {
					category.isOpened = action === 'open';
				}
				break;
			}
		}
	}

	selectAll(event: MatCheckboxChange, category: ManagementCategoryWithTags) {
		event.source._elementRef.nativeElement.dispatchEvent(new MouseEvent('click', { bubbles: false }));
		category.checked = event.checked;
		if (category.tags) {
			category.tags.forEach(tag => {
				tag.checked = event.checked;
				this.checkItem(event.checked, tag);
			});
		}
	}
	checkItem(checked: boolean, tag: ManagementTagWithSelect, category?: ManagementCategoryWithTags) {
		if (checked) {
			this.selectedTags.push(tag);
		} else {
			this.selectedTags = this.selectedTags.filter(selectedItem => selectedItem.id !== tag.id);
		}

		if (category) {
			category.checked = category.tags?.length === category.tags?.filter(tag => tag.checked).length;
		}
	}
	indeterminate(category: ManagementCategoryWithTags): boolean {
		if (category && category.tags) {
			const checkedItemsCount = category.tags.filter(tag => tag.checked).length;
			return checkedItemsCount !== category.tags.length && checkedItemsCount > 0 && !category.checked;
		}
		return false;
	}
	deselectAll() {
		this.categories?.forEach(category => {
			category.checked = false;
			category.tags?.forEach(tag => (tag.checked = false));
		});
		this.selectedTags = [];
	}

	areTagsRequired(): boolean {
		// Collect all selected subscription values
		let selectedSubscriptions: string[] = [];
		this.availableSubscriptionGroups.forEach(asg => {
			this.subscriptionPackagesGroups[asg].forEach(sp => {
				if (sp.checked && sp.value) {
					selectedSubscriptions.push(sp.value);
				}
			});
		});

		// If no subscriptions selected, tags are required
		if (selectedSubscriptions.length === 0) {
			return true;
		}

		// Check if ALL selected subscriptions are ONLY Sales and/or Bankruptcies
		const onlySalesOrBankruptcies = selectedSubscriptions.every(
			sub => sub === 'SALES' || sub === 'BANKRUPTCIES'
		);

		// Tags are NOT required if only sales/bankruptcies, otherwise they ARE required
		return !onlySalesOrBankruptcies;
	}

	subscribe(formDirective?: FormGroupDirective) {
		const period = this.selectedSubscriptionPeriod.replace(/\D/g, '');
		let selectedSubscriptions: string[] = [];
		let selectedSubscriptionsInGroup: string[] = [];
		let discountPackages = 0;
		this.availableSubscriptionGroups.forEach(asg => {
			selectedSubscriptionsInGroup = [];
			this.subscriptionPackagesGroups[asg].some(spg => {
				if (spg.checked && spg.value) {
					if (spg.type === 'discount') {
						selectedSubscriptionsInGroup = [spg.value];
						discountPackages++;
						return true;
					} else {
						selectedSubscriptionsInGroup.push(spg.value);
					}
				}
				return false;
			});
			selectedSubscriptions.push.apply(selectedSubscriptions, selectedSubscriptionsInGroup);
		});

		if (this.discountPackage && this.discountPackage.value && discountPackages === this.availableSubscriptionGroups.length) {
			selectedSubscriptions = [this.discountPackage.value];
		}

		const formValue = this.formGroup.value;

		const billingInformation: TenderiMkBillingInformationDto = {
			paymentOption: formValue.paymentOption,
			clientName: formValue.billingClientName,
			taxId: formValue.billingTaxId,
			foreignCountry: formValue.billingForeignCountry,
			country: formValue.billingCountry,
			email: formValue.billingEmail,
			contactAddress: formValue.billingContactAddress,
			contactCity: formValue.billingContactCity,
			contactPerson: formValue.billingContactPerson,
			contactPhone: formValue.billingContactPhone,
		};

		const tenderiDto: TenderiMkRequest = {
			clientName: formValue.clientName,
			taxId: formValue.taxId,
			foreignCountry: formValue.foreignCountry,
			country: formValue.country,
			email: formValue.email,
			contactAddress: formValue.contactAddress,
			contactCity: formValue.contactCity,
			contactPerson: formValue.contactPerson,
			contactPhone: formValue.contactPhone,
			firstName: formValue.firstName,
			lastName: formValue.lastName,
			clientTagIds: this.selectedTags.map(tag => tag.id),
			clientSubscriptions: selectedSubscriptions.map(ss => ({
				type: ss as SubscriptionModel.TypeEnum,
				duration: parseInt(period, 10),
			})),
			billingInformation,
		};

		this.apiLoading.contactForm = true;
		this.apiNotification
			.sendAccountAndClientInfoForSubscription(tenderiDto, this.utils.currentLanguage.toUpperCase())
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('LANDING.' + this.resellerSettingsContext.reseller.name.toUpperCase() + '.PRICING_SUBSCRIPTION_SUCCESS'),
						type: 'SUCCESS',
					});
					this.apiLoading.contactForm = false;
					this.formGroup.reset();
					if (formDirective) {
						formDirective.resetForm();
					}
					this.deselectAll();
					this.openSnackBar(this.translate.instant('LANDING.' + this.resellerSettingsContext.reseller.name.toUpperCase() + '.PRICING_SUBSCRIPTION_SUCCESS'), 'OK');

					setTimeout(() => {
						window.location.replace('/');
					}, 10000);
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.ACTION_ERROR'),
						type: 'ERROR',
					});
					this.apiLoading.contactForm = false;
					this.openSnackBar(this.translate.instant('NOTIFICATIONS.ACTION_ERROR'), 'OK');
				},
			});
	}

	openSnackBar(message: string, action: string) {
		this._snackBar.open(message, action);
	}
}
